
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator
} from '@/components/ui/input-otp';
import { Shield, ShieldCheck, RefreshCw, Phone, InfoIcon } from 'lucide-react';
import { sendOTP, verifyOTP, formatPhoneNumber } from '@/utils/twilioService';
import { sanitizeInput } from '@/utils/authUtils';

interface OTPVerificationProps {
  phone: string;
  onVerify: () => void;
  onResend: () => void;
  remainingTime: number;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ 
  phone, 
  onVerify, 
  onResend, 
  remainingTime 
}) => {
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [formattedPhone, setFormattedPhone] = useState('');
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);

  useEffect(() => {
    // Format the phone number with proper country code display
    setFormattedPhone(formatPhoneNumber(phone));
  }, [phone]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (isBlocked && blockTimeRemaining > 0) {
      intervalId = setInterval(() => {
        setBlockTimeRemaining(time => {
          if (time <= 1) {
            setIsBlocked(false);
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }
    
    return () => clearInterval(intervalId);
  }, [isBlocked, blockTimeRemaining]);

  const handleVerify = async () => {
    if (isBlocked) {
      toast.error(`Too many failed attempts. Please wait ${blockTimeRemaining} seconds.`);
      return;
    }

    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    setIsVerifying(true);
    
    try {
      // Sanitize OTP input
      const sanitizedOTP = sanitizeInput(otp);
      const isValid = await verifyOTP(phone, sanitizedOTP);
      
      if (isValid) {
        toast.success('Phone number verified successfully');
        setVerificationAttempts(0);
        onVerify();
      } else {
        const newAttempts = verificationAttempts + 1;
        setVerificationAttempts(newAttempts);
        
        if (newAttempts >= 3) {
          setIsBlocked(true);
          setBlockTimeRemaining(60); // Block for 60 seconds after 3 failed attempts
          toast.error('Too many failed verification attempts. Please wait 60 seconds before trying again.');
        } else {
          toast.error('Invalid verification code. Please try again.');
        }
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      toast.error('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="glass-panel rounded-xl p-6 animate-scale-in max-w-md mx-auto">
      <div className="flex items-center justify-center mb-4">
        <div className="h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center">
          <Shield className="h-8 w-8 text-apple-blue" />
        </div>
      </div>

      <h3 className="text-xl font-semibold text-apple-text mb-2 text-center">Phone Verification</h3>
      <p className="text-apple-text-secondary mb-6 text-center">
        Enter the 6-digit code sent to <strong>{formattedPhone || phone || "your phone"}</strong>
      </p>

      <div className="flex justify-center mb-8">
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={(value) => setOtp(value)}
          containerClassName="gap-2"
          render={({ slots }) => (
            <InputOTPGroup>
              {slots.map((slot, index) => (
                <React.Fragment key={index}>
                  <InputOTPSlot 
                    {...slot} 
                    index={index}
                    className="w-10 h-12 text-center text-xl font-semibold rounded-md border border-gray-300 focus:border-apple-blue focus:ring-1 focus:ring-apple-blue outline-none transition-all"
                  />
                  {index !== slots.length - 1 && index % 3 === 2 && (
                    <InputOTPSeparator />
                  )}
                </React.Fragment>
              ))}
            </InputOTPGroup>
          )}
        />
      </div>

      <div className="flex justify-center mb-6">
        <button
          onClick={onResend}
          disabled={remainingTime > 0}
          className={`text-sm flex items-center ${
            remainingTime > 0
              ? 'text-apple-text-secondary cursor-default'
              : 'text-apple-blue hover:underline'
          }`}
        >
          {remainingTime > 0 ? (
            `Resend code in ${remainingTime}s`
          ) : (
            'Resend code'
          )}
        </button>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleVerify}
          className="w-full py-2.5 rounded-lg bg-apple-blue text-white font-medium transition-all hover:bg-opacity-90 flex items-center justify-center"
          disabled={isVerifying}
        >
          {isVerifying ? (
            <>
              <RefreshCw className="animate-spin mr-2 h-4 w-4" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </button>
      </div>

      <div className="mt-4 flex items-start text-sm text-apple-text-secondary p-3 bg-gray-50 rounded-lg">
        <InfoIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-apple-blue" />
        <span>
          In this demo, the verification code is 123456.
          <br />
          In production, this would be sent via SMS to your phone.
        </span>
      </div>
    </div>
  );
};

export default OTPVerification;
